'use client';

import React, { FC, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { usePathname } from 'next/navigation';
import { LoginSchema } from '@/src/v2/layout/Auth/LoginModal/LoginSchema';
import { signInService } from '@/src/services/auth';
import { setUser, setIsLogging } from '@/src/v2/store/slices/user/UserSlice';
import { useAppDispatch } from '@/src/v2/hooks/useAppDispatch/UseAppDispatch';
import { useAuthContext } from '@/src/v2/layout/Auth/context/AuthContext';
import { useAppService } from '@/src/v2/hooks/useAppService/UseAppService';
import { socialAuthService } from '@/src/services/auth';
import style from './login.module.scss';
import { IUserResponseV2 } from '@/src/v2/service/v2/graphql/generated/gql-api.generated';
import { getQueryClient } from '@/src/v2/service/graphql/api';
import {
  GetEventsForChannel,
  //getQueryClientV2,
  GetDashboardV2,
  GetMeAbout,
  GetRevenueChart,
  GetFollowerListV2,
  GetAllChannels,
  GetFollowingListV2,
  GetCategoriesV2,
  GetChannel,
  GetUserByUsername,
} from '@/src/v2/service/v2/graphql/apiv2';
import dynamic from 'next/dynamic';
import { useClientContext } from '@/src/app/client.provider';
import { queryConfig } from '@/src/utils/cache/queryConfig';
import LockTimer from './LockTimer';
const Button = dynamic(() => import('@/src/v2/components/Button/Button'), {
  ssr: false,
});
const Icon = dynamic(() => import('@/src/v2/components/Icon'), { ssr: false });
const ControlledInput = dynamic(
  () => import('@/src/v2/components/Form/ControlledInput/ControlledInput'),
  { ssr: false }
);
const ControlledForm = dynamic(
  () => import('@/src/components/clean/Form/ControlledForm/ControlledForm'),
  { ssr: false }
);

type Props = {
  onClose: () => void;
};
const LoginForm: FC<Props> = ({ onClose }) => {
  const pathname = usePathname();
  const {
    handleSubmit,
    formState: { isValid },
  } = useFormContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [disableLogin, setDisableLogin] = useState(false);
  const { updateReload } = useClientContext();
  const {
    setMode,
    attempts,
    setAttempts,
    counter,
    setCounter,
    MAX_LOGIN_ATTEMPT,
  } = useAuthContext();
  const dispatch = useAppDispatch();
  const { getApiService } = useAppService();
  const prefetchDataDashboard = (user: IUserResponseV2) => {
    if (user) {
      const queryClient = getQueryClient();
      queryClient.prefetchQuery(
        [`getEventsForChannel${user?.channel?.id}`],
        () => GetEventsForChannel({ channelId: user?.channel?.id }),
        {
          ...queryConfig,
        }
      );
      queryClient.prefetchQuery(
        [`GetDashboardV2-${user?.id}-${user?.channel?.id}`],
        () =>
          GetDashboardV2({
            userId: user?.id,
            channelId: user?.channel?.id,
          }),
        {
          ...queryConfig,
        }
      );
      queryClient.prefetchQuery(
        [`GetMeAbout-${user?.id}`],
        () => GetMeAbout({ userId: user?.id }),
        {
          ...queryConfig,
        }
      );
      queryClient.prefetchQuery(
        [`getRevenueChart`],
        () =>
          GetRevenueChart({
            channelId: user?.channel?.id,
            startDate: new Date().toISOString().split('T')[0],
            endDate: new Date().toISOString().split('T')[0],
            type: 'revenue',
          }),
        {
          ...queryConfig,
        }
      );
      queryClient.prefetchQuery(
        [`getFollowerListV2`],
        () =>
          GetFollowerListV2({
            getFollowerInput: {
              channelId: user.channel.id,
              limit: 10,
              page: 0,
              keyword: '',
            },
          }),
        {
          ...queryConfig,
        }
      );
      queryClient.prefetchQuery([`getAllChannels`], () => GetAllChannels(), {
        ...queryConfig,
      });
      queryClient.prefetchQuery(
        [`GetFollowingListV2`],
        () =>
          GetFollowingListV2({
            getFollowerInput: {
              channelId: user.channel.id,
              limit: 10,
              page: 0,
              keyword: '',
            },
          }),
        {
          ...queryConfig,
        }
      );
      queryClient.prefetchQuery([`GetCategoriesV2`], () => GetCategoriesV2(), {
        ...queryConfig,
      });
      queryClient.prefetchQuery(
        [
          `GetChannelByUsername${user?.channel?.name}`,
          { username: user?.channel?.name },
        ],
        () => GetChannel({ username: user?.channel?.name }),
        {
          ...queryConfig,
        }
      );
      queryClient.prefetchQuery(
        [`GetFollowerByChannel-${user?.channel?.name}`],
        () =>
          GetFollowingListV2({
            getFollowerInput: {
              channelId: user?.channel?.id,
              limit: 10,
              page: 0,
              keyword: '',
            },
          }),
        {
          ...queryConfig,
        }
      );
    }
  };
  const handleFailedLogin = () => {
    const lockoutExpiry = Date.now() + 60 * 1000; // 1 minute lockout
    localStorage.setItem('lockoutExpiry', lockoutExpiry.toString());
    setDisableLogin(true);
    setError('');
  };
  useEffect(() => {
    const onEnterHandler = (event: any) => {
      if (event.code === 'Enter' && !disableLogin) {
        onLogin();
      }
    };
    document.addEventListener('keydown', onEnterHandler);
    return () => {
      document.removeEventListener('keydown', onEnterHandler);
    };
  }, [isValid]);

  useEffect(() => {
    if (attempts >= 5) {
      handleFailedLogin();
      setDisableLogin(true);
    } else if (attempts === 0) {
      setDisableLogin(false);
    }
  }, [attempts]);
  useEffect(() => {
    const lockoutExpiry = localStorage.getItem('lockoutExpiry');
    const now = Date.now();
    if (lockoutExpiry && Number(lockoutExpiry) > now) {
      setDisableLogin(true);
      setError('');
    }
  }, []);

  const onLogin = () => {
    setIsLoading(true);
    dispatch(setIsLogging(true));
    if (disableLogin) {
      return;
    }

    handleSubmit(
      async (data: any) => {
        const isEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
          data.email
        );
        const username = isEmail
          ? data.email
          : await GetUserByUsername({ username: data.email })
              .then((data) => data?.getUserByUsernameV2?.email)
              .catch((error) => {
                console.error('Failed to lookup username:', error);
                return data.email; // fallback to original input
              });

        signInService(username, data.password)
          .then((user) => {
            setError('');
          })
          .then<IUserResponseV2>(() => {
            return getApiService()
              .GetCurrentUser({ token: localStorage.getItem('ptoken') })
              .then((userResponse) => {
                setUser(userResponse.getMeV2);
                dispatch(setUser(userResponse.getMeV2));
                return userResponse.getMeV2;
              });
          })
          .then((user) => {
            prefetchDataDashboard(user);
            updateReload();
            setUser(user);
            dispatch(setUser(user));
            setAttempts(0);
            onClose();
          })
          .catch((err) => {
            if (!localStorage.getItem('ptoken')) {
              // console.log('err', err);
              setIsLoading(false);
              dispatch(setIsLogging(false));
              setError('The Credentials Entered Are Invalid');
              setAttempts(attempts + 1);
              if (attempts == 5) {
                setCounter(60);
              }
            } else {
              setIsLoading(false);
              dispatch(setIsLogging(false));
              updateReload();
              setAttempts(0);
              onClose();
              if (pathname !== '/') {
                window.location.reload();
              }
            }
          });
      },
      (error) => {
        setIsLoading(false);
        dispatch(setIsLogging(false));
      }
    )();
  };

  const googleLogin = () => {
    dispatch(setIsLogging(true));
    localStorage.setItem('login_from', 'login');
    localStorage.setItem('login_type', 'Google');
    localStorage.setItem('klic_previous_url', window.location.href);

    socialAuthService('Google');
  };

  const discordLogin = () => {
    dispatch(setIsLogging(true));
    localStorage.setItem('login_type', 'Discord');
    localStorage.setItem('login_from', 'login');
    socialAuthService('Discord');
  };

  const twitterLogin = () => {
    dispatch(setIsLogging(true));
    localStorage.setItem('login_type', 'Twitter');
    localStorage.setItem('login_from', 'login');
    socialAuthService('Twitter');
  };

  return (
    <div className={style['login-modal__container']}>
      <div className={style['login-modal__form']}>
        <ControlledInput
          id={'email'}
          placeholder={'Enter email or Username'}
          name={'email'}
          label="Email or Username"
        />
        <ControlledInput
          id={'password'}
          type={'password'}
          placeholder={'Enter password'}
          label={`Password`}
          name={'password'}
        />
        <div
          className={style['login-modal__form__help']}
          onClick={() => setMode('forgot password')}
        >
          Forgot Password?
        </div>
        <div className={style['login-modal__form__actions']}>
          {disableLogin && <LockTimer setDisableLogin={setDisableLogin} />}
          {!disableLogin && (
            <Button
              type={'secondary-v2'}
              disabled={!isValid || disableLogin}
              loading={isLoading}
              label={'LOG IN'}
              onClick={() => onLogin()}
              className={style['login-modal__form__loginbtn']}
            />
          )}
          <div style={{ margin: '16px 0' }}>
            <span>{error}</span>
            {attempts > 0 && MAX_LOGIN_ATTEMPT > attempts && !disableLogin && (
              <span>{MAX_LOGIN_ATTEMPT - attempts} attempts is remaining.</span>
            )}
          </div>
        </div>
        {/* <div className={style['login-modal__form__hints']}>
          <span>{`Don't have account?`}</span>
          <span onClick={() => setMode('sign up')}>Sign up!</span>
        </div> */}

        <div className={style['login-modal__auth_separator']}>
          <div className={style['login-modal__auth_separator__left']}></div>
          <div className={style['login-modal__auth_separator__center']}>
            Login With
          </div>
          <div className={style['login-modal__auth_separator__right']}></div>
        </div>
        <div className={style['login-modal__social']}>
          <div>
            <Icon
              icon={'googleIcon'}
              width={30}
              height={30}
              onClick={() => {
                disableLogin ? console.log('Disable') : googleLogin();
              }}
            />
          </div>
          {(process.env.NEXT_PUBLIC_SOCIAL_LOGIN || 'false') === 'true' && (
            <>
              <div>
                <Icon
                  icon={'discord'}
                  width={30}
                  height={30}
                  onClick={() => {
                    disableLogin ? console.log('Disable') : discordLogin();
                  }}
                />
              </div>
              <div>
                <Icon
                  icon={'footerTwitter'}
                  width={30}
                  height={30}
                  onClick={() => {
                    disableLogin ? console.log('Disable') : twitterLogin();
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const LoginModal: FC<Props> = ({ onClose }) => {
  return (
    <ControlledForm schema={LoginSchema}>
      <LoginForm onClose={onClose} />
    </ControlledForm>
  );
};

export default LoginModal;
